import React, { useEffect, useMemo, useState } from "react";
import {
  Search, Inbox, Phone, Mail, Trash2, CheckCircle2, Clock, AlertCircle, RefreshCw,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "closed", label: "Closed" },
];

const STATUS_META = {
  new: { color: "#7A2021", bg: "#FDF2F2", icon: AlertCircle, label: "New" },
  contacted: { color: "#92400E", bg: "#FEF7E6", icon: Clock, label: "Contacted" },
  closed: { color: "#166534", bg: "#ECFDF5", icon: CheckCircle2, label: "Closed" },
};

export default function EnquiriesView({ onUnauthorized }) {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ new: 0, contacted: 0, closed: 0, total: 0 });
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: enquiries }, { data: s }] = await Promise.all([
        api.get("/enquiries"),
        api.get("/enquiries/stats"),
      ]);
      setList(enquiries || []);
      setStats(s || stats);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      else toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    return list.filter((e) => {
      if (tab !== "all" && e.status !== tab) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        e.name.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.phone.toLowerCase().includes(q) ||
        e.area.toLowerCase().includes(q) ||
        e.message.toLowerCase().includes(q)
      );
    });
  }, [list, tab, query]);

  const updateStatus = async (id, statusVal) => {
    try {
      const { data } = await api.patch(`/enquiries/${id}`, { status: statusVal });
      setList((cur) => cur.map((x) => (x.id === id ? data : x)));
      if (selected?.id === id) setSelected(data);
      const prev = list.find((x) => x.id === id);
      if (prev) {
        setStats((s) => ({
          ...s,
          [prev.status]: Math.max(0, (s[prev.status] || 0) - 1),
          [statusVal]: (s[statusVal] || 0) + 1,
        }));
      }
      toast.success(`Marked as ${statusVal}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      const prev = list.find((x) => x.id === id);
      setList((cur) => cur.filter((x) => x.id !== id));
      if (prev) {
        setStats((s) => ({
          ...s,
          [prev.status]: Math.max(0, (s[prev.status] || 0) - 1),
          total: Math.max(0, s.total - 1),
        }));
      }
      if (selected?.id === id) setSelected(null);
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="enquiries-view">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.28em" }}>
            Chamber dashboard
          </span>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-3" style={{ color: "#0A1128", fontWeight: 500 }}>
            Consultation Enquiries
          </h1>
        </div>
        <button onClick={load} className="btn-secondary" data-testid="admin-refresh-enquiries">
          <RefreshCw size={14} strokeWidth={1.6} /> Refresh
        </button>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px" style={{ backgroundColor: "#E2DCD0", border: "1px solid #E2DCD0" }}>
        <StatCard label="Total" value={stats.total} testid="stat-total" />
        <StatCard label="New" value={stats.new} accent="#7A2021" testid="stat-new" />
        <StatCard label="Contacted" value={stats.contacted} accent="#92400E" testid="stat-contacted" />
        <StatCard label="Closed" value={stats.closed} accent="#166534" testid="stat-closed" />
      </div>

      <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex gap-1" style={{ border: "1px solid #E2DCD0", backgroundColor: "#FFFFFF" }}>
          {STATUS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setTab(s.key)}
              className="px-5 py-3 text-xs uppercase transition-colors"
              data-testid={`tab-${s.key}`}
              style={{
                letterSpacing: "0.18em",
                backgroundColor: tab === s.key ? "#0A1128" : "transparent",
                color: tab === s.key ? "#F9F6F0" : "#0A1128",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="relative md:w-80" style={{ border: "1px solid #E2DCD0", backgroundColor: "#FFFFFF" }}>
          <Search size={16} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#64748B" }} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, area…"
            className="w-full pl-10 pr-4 py-3 bg-transparent outline-none text-sm"
            style={{ color: "#0A1128" }}
            data-testid="admin-search"
          />
        </div>
      </div>

      <div className="mt-8" style={{ border: "1px solid #E2DCD0", backgroundColor: "#FFFFFF" }}>
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 text-[10px] uppercase"
          style={{ color: "#64748B", letterSpacing: "0.2em", borderBottom: "1px solid #E2DCD0" }}
        >
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Area</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-2 text-right">Status</div>
        </div>

        {loading ? (
          <EmptyState text="Loading enquiries…" />
        ) : filtered.length === 0 ? (
          <EmptyState text={list.length === 0 ? "No enquiries yet." : "No enquiries match your filters."} />
        ) : (
          <ul>
            {filtered.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => setSelected(e)}
                  className="w-full text-left px-6 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center transition-colors hover:bg-[#F9F6F0]"
                  style={{ borderBottom: "1px solid #E2DCD0" }}
                  data-testid={`enquiry-row-${e.id}`}
                >
                  <div className="md:col-span-3">
                    <div className="font-serif-display text-xl" style={{ color: "#0A1128" }}>{e.name}</div>
                    <div className="md:hidden text-xs mt-1" style={{ color: "#64748B" }}>{e.area} · {formatDate(e.created_at)}</div>
                  </div>
                  <div className="md:col-span-3 text-sm space-y-0.5" style={{ color: "#334155" }}>
                    <div className="flex items-center gap-2"><Mail size={13} strokeWidth={1.6} style={{ color: "#7A2021" }} /> <span className="truncate">{e.email}</span></div>
                    <div className="flex items-center gap-2"><Phone size={13} strokeWidth={1.6} style={{ color: "#7A2021" }} /> {e.phone}</div>
                  </div>
                  <div className="md:col-span-2 hidden md:block text-sm" style={{ color: "#0A1128" }}>{e.area}</div>
                  <div className="md:col-span-2 hidden md:block text-xs" style={{ color: "#64748B" }}>{formatDate(e.created_at)}</div>
                  <div className="md:col-span-2 flex md:justify-end">
                    <StatusPill status={e.status} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 flex" data-testid="enquiry-drawer">
          <div className="flex-1 bg-black/40" onClick={() => setSelected(null)} />
          <div className="w-full max-w-xl h-full overflow-y-auto" style={{ backgroundColor: "#FFFFFF", borderLeft: "1px solid #E2DCD0" }}>
            <div className="p-8 flex items-start justify-between" style={{ borderBottom: "1px solid #E2DCD0" }}>
              <div>
                <span className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.24em" }}>{selected.area}</span>
                <h3 className="font-serif-display text-3xl mt-2" style={{ color: "#0A1128", fontWeight: 500 }}>{selected.name}</h3>
                <div className="mt-1 text-xs" style={{ color: "#64748B" }}>Received {formatDate(selected.created_at, true)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-xs uppercase" style={{ color: "#64748B", letterSpacing: "0.2em" }} data-testid="drawer-close">
                Close
              </button>
            </div>

            <div className="p-8 space-y-6">
              <Detail label="Email"><a href={`mailto:${selected.email}`} className="hover:underline" style={{ color: "#0A1128" }}>{selected.email}</a></Detail>
              <Detail label="Phone"><a href={`tel:${selected.phone}`} className="hover:underline" style={{ color: "#0A1128" }}>{selected.phone}</a></Detail>
              <Detail label="Message"><p className="whitespace-pre-wrap" style={{ color: "#334155", lineHeight: 1.7 }}>{selected.message}</p></Detail>

              <div>
                <div className="field-label">Status</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.keys(STATUS_META).map((k) => (
                    <button
                      key={k}
                      onClick={() => updateStatus(selected.id, k)}
                      className="px-4 py-2 text-xs uppercase"
                      style={{
                        letterSpacing: "0.16em",
                        border: `1px solid ${STATUS_META[k].color}`,
                        backgroundColor: selected.status === k ? STATUS_META[k].color : "transparent",
                        color: selected.status === k ? "#FFFFFF" : STATUS_META[k].color,
                      }}
                      data-testid={`drawer-status-${k}`}
                    >
                      {STATUS_META[k].label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex items-center gap-3" style={{ borderTop: "1px solid #E2DCD0" }}>
                <a href={`tel:${selected.phone}`} className="btn-secondary"><Phone size={14} strokeWidth={1.6} /> Call</a>
                <a href={`mailto:${selected.email}`} className="btn-secondary"><Mail size={14} strokeWidth={1.6} /> Email</a>
                <button onClick={() => remove(selected.id)} className="ml-auto inline-flex items-center gap-2 text-xs uppercase" style={{ color: "#7A2021", letterSpacing: "0.18em" }} data-testid="drawer-delete">
                  <Trash2 size={14} strokeWidth={1.6} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent = "#0A1128", testid }) {
  return (
    <div className="p-6 md:p-8" style={{ backgroundColor: "#FFFFFF" }} data-testid={testid}>
      <div className="text-[10px] uppercase" style={{ color: "#64748B", letterSpacing: "0.24em" }}>{label}</div>
      <div className="font-serif-display text-4xl md:text-5xl mt-2" style={{ color: accent, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <span className="pill" style={{ color: meta.color, backgroundColor: meta.bg }}>
      <Icon size={12} strokeWidth={1.8} /> {meta.label}
    </span>
  );
}

function EmptyState({ text }) {
  return (
    <div className="p-16 text-center">
      <Inbox size={28} strokeWidth={1.4} className="mx-auto" style={{ color: "#C5A059" }} />
      <p className="mt-4 text-sm" style={{ color: "#64748B" }}>{text}</p>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="field-label">{label}</div>
      <div className="text-base mt-1" style={{ color: "#0A1128" }}>{children}</div>
    </div>
  );
}

function formatDate(iso, withTime) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    if (!withTime) return date;
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
}
